import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import styles from "./AddressesPage.module.css";
import Header from "../components/Header";
import AddressList from "../components/AddressList";
import AddressInfoItem from "../components/AddressInfoItem";
import Modal from "components/Modal";
import ComparisonModal from "../components/ComparisonModal";
import Button from "../components/Button";
import Loader from "components/Loader";
import AddressModal from "components/AddressModal";
import { useAddresses } from "./useAddressesPage";
import WarningPrimaryModal from "components/WarningPrimaryModal";
import { useAccount } from "utils/AccountInfoContext";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";

export function AddressesPage() {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { iboAddressId } = useParams();
  const { billingData } = useAccount();
  const { t } = useTranslation();

  const {
    data: addressData,
    isError,
    isLoading,
    refetch,
    addAddress,
    addResolveAddress,
    updateAddress,
    deleteAddress,
  } = useAddresses();

  const { statesData } = useAccount();

  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [validationFailed, setValidationFailed] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isAddingAddress, setIsAddingAddress] = useState(true);
  const [isAddInComparison, setIsAddInComparison] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showBillingWarningModal, setShowBillingWarningModal] = useState(false);
  const [pendingPrimaryIndex, setPendingPrimaryIndex] = useState(null);
  const [previousAddresses, setPreviousAddresses] = useState([]);
  const [defaultShippingIndex, setDefaultShippingIndex] = useState(null);
  const [isTogglerDisabled, setIsTogglerDisabled] = useState(false);

  const [address, setAddress] = useState({
    iboAddressId: 0,
    recipient: "",
    country: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postalCode: "",
    latitude: 0,
    longitude: 0,
    isVerified: 0,
    primary: 0,
    mailing: 0,
    shipping: 0,
    billing: 0,
    bitwiseType: 0,
  });

  const [suggestedAddress, setSuggestedAddress] = useState({
    recipient: "",
    country: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postalCode: "",
    latitude: "",
    longitude: "",
    isVerified: 1,
    primary: 0,
    mailing: 0,
    shipping: 0,
    billing: 0,
    bitwiseType: 0,
  });

  const ADDRESS_TYPES = {
    primary: 1,
    mailing: 2,
    shipping: 4,
    billing: 8,
  };

  const calculateBitwiseType = (types) => {
    let bitwiseType = 0;
    if (types?.primary) bitwiseType |= ADDRESS_TYPES?.primary;
    if (types?.mailing) bitwiseType |= ADDRESS_TYPES?.mailing;
    if (types?.shipping) bitwiseType |= ADDRESS_TYPES?.shipping;
    if (types?.billing) bitwiseType |= ADDRESS_TYPES?.billing;
    return bitwiseType;
  };

  useEffect(() => {
    if (addressData) {
      const formattedAddresses = addressData.map((addr) => ({
        iboAddressId: addr.iboAddressId,
        recipient: addr.recipient,
        line1: addr.line1,
        line2: addr.line2,
        city: addr.city,
        state: addr.state,
        postalCode: addr.postalCode,
        country: getCountryCode(addr.country),
        primary: addr.primary,
        shipping: addr.shipping,
        mailing: addr.mailing,
        billing: addr.billing,
        bitwiseType: addr.shipping,
        latitude: addr.latitude,
        longitude: addr.longitude,
        isVerified: addr.isVerified,
      }));
      setAddresses(formattedAddresses);
    }
  }, [addressData]);

  // Find iboAddressId to update
  useEffect(() => {
    if (iboAddressId) {
      const address = addressData.find(
        (addr) => addr.iboAddressId === parseInt(iboAddressId, 10)
      );
      if (address) {
        setSelectedAddress(address);
        setIsAddingAddress(false);
        setShowModal(true);
      }
    } else {
      setShowModal(false);
      setSelectedAddress(null);
    }
  }, [iboAddressId, addressData]);

  const handleChange = (field, value) => {
    setAddress((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (showModal && address.iboAddressId) {
      setSelectedAddress(address);
    }
  }, [showModal, address]);

  const capitalizeFirstLetter = (string) => {
    return string.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatPostalCode = (postalCode) => {
    return postalCode ? postalCode.slice(0, 5) : "";
  };

  const getCountryCode = (country) => {
    switch (country) {
      case "United States":
        return "US";
      case "Canada":
        return "CA";
      case "Cayman Islands":
        return "KY";
      case "Jamaica":
        return "JM";
      case "Australia":
        return "AU";
      case "New Zealand":
        return "NZ";
      default:
        return country;
    }
  };

  const extractStateAbbreviation = (state) => {
    if (state.includes(" - ")) {
      return state.split(" - ")[0];
    }
    return state;
  };

  // Api calls to resolve address and show suggested address
  async function handleResolveAddress(address, isAdd) {
    const resolveAddressData = {
      line1: address.line1,
      line2: address.line2,
      city: address.city,
      state: extractStateAbbreviation(address.state),
      country: getCountryCode(address.country),
      postalCode: address.postalCode,
    };

    setLoading(true);

    try {
      const resolvedData = await addResolveAddress.mutateAsync(
        resolveAddressData
      );

      const newSuggestedAddress = {
        recipient: address.recipient,
        line1: resolvedData.line1,
        line2: resolvedData.line2 || address.line2 || null,
        city: resolvedData.city,
        state: extractStateAbbreviation(resolvedData.state),
        country: getCountryCode(resolvedData.country),
        postalCode: resolvedData.postalCode,
        latitude: resolvedData.latitude || address.latitude || 0,
        longitude: resolvedData.longitude || address.longitude || 0,
        types: {
          primary: 0,
          mailing: 0,
          shipping: 0,
          billing: 0,
          bitwiseType: 0,
        },
      };

      if (resolvedData.suggestions.length > 0) {
        const errors = {};
        if (
          resolvedData.suggestions.includes(
            "Invalid PO Box or House Number" ||
              "Insufficient Address Information"
          )
        ) {
          errors.line1 = true;
        }
        setValidationErrors({
          ...errors,
          suggestions: resolvedData.suggestions,
        });
        setValidationFailed(true);
        setLoading(false);
        return;
      }

      setAddress((prevState) => {
        return isAdd
          ? {
              ...prevState,
              ...address,
            }
          : {
              ...prevState,
              ...selectedAddress,
              ...address,
            };
      });

      setSuggestedAddress((prevState) => {
        return isAdd
          ? {
              ...prevState,
              ...newSuggestedAddress,
              isVerified: 1,
            }
          : {
              ...prevState,
              ...selectedAddress,
              ...newSuggestedAddress,
              isVerified: 1,
            };
      });

      setLoading(false);

      setShowModal(false);
      setShowComparisonModal(true);
      setIsAddInComparison(isAdd);
    } catch (error) {
      console.error(t("Error resolving address"), error);
      setLoading(false);
    }
  }

  // Handles adding address last step
  // -being use in handleUseSuggested and handleUseOriginal
  async function handleAddAddress(address) {
    const bitwiseType = calculateBitwiseType(address.types);

    const newAddress = {
      recipient: capitalizeFirstLetter(address.recipient),
      country: getCountryCode(address.country),
      line1: address.line1,
      line2: address.line2,
      city: address.city,
      state: extractStateAbbreviation(address.state),
      postalCode: address.postalCode,
      latitude: address.latitude,
      longitude: address.longitude,
      isVerified: address.isVerified,
      types: {
        primary: address.primary ? 1 : 0,
        mailing: address.mailing ? 1 : 0,
        shipping: address.shipping ? 1 : 0,
        billing: address.billing ? 1 : 0,
        bitwiseType: bitwiseType,
      },
    };

    try {
      await addAddress.mutateAsync(newAddress);
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error(t("Error adding address:"), error);
    }
  }

  // Triggers use original address click
  const handleUseOriginal = () => {
    const updatedAddress = {
      ...address,
      latitude: suggestedAddress.latitude,
      longitude: suggestedAddress.longitude,
      isVerified: 0,
    };

    if (isAddingAddress) {
      handleAddAddress(updatedAddress);
      resetForm();
    } else {
      setAddress((prevState) => {
        const updatedAddress = {
          ...prevState,
          ...selectedAddress,
          ...address,
          latitude: suggestedAddress.latitude,
          longitude: suggestedAddress.longitude,
          isVerified: 0,
        };
        handleUpdateAddress(updatedAddress);

        return updatedAddress;
      });
    }

    history.push(`/addresses`);
    setShowComparisonModal(false);
    resetForm();
  };

  // Triggers use suggested address click
  const handleUseSuggested = () => {
    if (isAddingAddress) {
      const newAddress = {
        ...suggestedAddress,

        latitude: suggestedAddress.latitude,
        longitude: suggestedAddress.longitude,
        postalCode: suggestedAddress.postalCode,
        isVerified: 1,
      };
      handleAddAddress(newAddress);
      resetForm();
    } else {
      const updatedAddress = {
        ...suggestedAddress,

        latitude: suggestedAddress.latitude,
        longitude: suggestedAddress.longitude,
        postalCode: suggestedAddress.postalCode,
        isVerified: 1,
      };

      handleUpdateAddress(updatedAddress);
    }

    history.push(`/addresses`);
    setShowComparisonModal(false);
    resetForm();
  };

  // Update address - triggers edit button
  function handleEditAddress(iboAddressId) {
    const address = addressData.find(
      (addr) => addr.iboAddressId === iboAddressId
    );

    if (!address) {
      return;
    }
    if (address.primary === 1 && billingData === true) {
      setPreviousAddresses(addresses); // Save the previous state
      setShowBillingWarningModal(true);
      return;
    }

    history.push(`/addresses/edit/${iboAddressId}`);

    setIsAddingAddress(false);
    setShowModal(true);

    const updatedCountry =
      address.country === "US" ? "United States" : address.country;

    const stateInfo = statesData.find(
      (state) => state.abbreviation === address.state
    );
    // const formattedState = stateInfo
    //   ? `${stateInfo.abbreviation} - ${stateInfo.state}`
    //   : address.state;

    setSelectedAddress({
      ...address,
      iboAddressId: address.iboAddressId,
      country: updatedCountry || "",
      // state: formattedState,
    });
  }

  // Handles updating address last step
  // being used in handleUseSuggested and handleUseOriginal
  async function handleUpdateAddress(address) {
    if (!address.iboAddressId) {
      return;
    }

    const bitwiseType = calculateBitwiseType({
      primary: address.primary,
      mailing: address.mailing,
      shipping: address.shipping,
      billing: address.billing,
    });

    const updatedData = {
      line1: address.line1 || "",
      line2: address.line2 || null,
      city: address.city || "",
      state: extractStateAbbreviation(address.state) || "",
      country: getCountryCode(address.country) || "",
      postalCode: address.postalCode || "",
      recipient: address.recipient || "",
      latitude: address.latitude || 0,
      longitude: address.longitude || 0,
      isVerified: address.isVerified,
      types: {
        primary: address.primary || 0,
        mailing: address.mailing || 0,
        shipping: address.shipping || 0,
        billing: address.billing || 0,
        bitwiseType: bitwiseType,
      },
    };

    try {
      await updateAddress.mutateAsync({
        iboAddressId: address.iboAddressId,
        updateData: updatedData,
      });
      // refetch();
    } catch (error) {
      console.error(t("Error updating address"), error);
    }

    setIsAddInComparison(false);
    resetForm();
  }

  // Address error checking
  const handleValidateAddress = async (address, isAdd) => {
    // If the country is Jamaica or Cayman Islands, handle it differently
    if (address.country === "Jamaica" || address.country === "Cayman Islands") {
      const resolveAddressData = {
        line1: address.line1,
        line2: address.line2,
        city: address.city,
        state: extractStateAbbreviation(address.state),
        country: getCountryCode(address.country),
        postalCode: address.postalCode,
      };

      try {
        setLoading(true);

        // Resolve the address to get latitude and longitude
        const resolvedData = await addResolveAddress.mutateAsync(
          resolveAddressData
        );

        // Ignore any suggestions and use the original input with the resolved lat/long
        const newSuggestedAddress = {
          recipient: address.recipient,
          line1: address.line1,
          line2: address.line2 || null,
          city: address.city,
          state: address.state,
          country: address.country,
          postalCode: address.postalCode,
          latitude: resolvedData.latitude || address.latitude || 0,
          longitude: resolvedData.longitude || address.longitude || 0,
          isVerified: 1, // Mark it as verified since we're using resolved lat/long
        };

        // Set the state for both the original address and the suggested address
        setAddress((prevState) => ({
          ...prevState,
          ...address,
        }));

        setSuggestedAddress((prevState) => ({
          ...prevState,
          ...newSuggestedAddress,
        }));

        setLoading(false);
        setShowModal(false);
        setShowComparisonModal(true);
        setIsAddInComparison(isAdd);
      } catch (error) {
        console.error(t("Error resolving address"), error);
        setLoading(false);
      }

      return;
    }

    // Normal validation for other countries
    const validateAddress = (address, suggestions) => {
      const errors = {};

      if (
        !address.line1 ||
        (suggestions.length > 0 &&
          suggestions.includes(t("Invalid PO Box or House Number")))
      ) {
        errors.line1 = true;
      }

      if (!suggestions.includes(t("Invalid PO Box or House Number"))) {
        if (!address.city) {
          errors.city = true;
        }

        if (address.state === t("Select State") || !address.state) {
          errors.state = true;
        }

        if (address.country === t("Select Country") || !address.country) {
          errors.country = true;
        }

        if (!address.postalCode) {
          errors.postalCode = true;
        }
      }

      const hasFieldErrors = Object.keys(errors).some((key) => errors[key]);

      if (hasFieldErrors) {
        if (!suggestions || suggestions.length === 0) {
          suggestions = address.suggestions;
        }
        setValidationFailed(true);
        setValidationErrors({ ...errors, suggestions });
      } else {
        setValidationErrors([]);
      }

      return errors;
    };

    let suggestions = [];
    const errors = validateAddress(address, suggestions);

    if (Object.keys(errors).some((key) => errors[key])) {
      setValidationErrors(errors);
      setValidationFailed(true);
    } else {
      setValidationFailed(false);
      setValidationErrors([]);
      await handleResolveAddress(address, isAdd);
    }
  };

  // Triggers the Edit icon in comparison modal
  const handleComparisonEditClick = () => {
    setShowComparisonModal(false);
    setShowModal(true);
    if (isAddingAddress) {
      setIsAddingAddress(true);
    } else {
      setIsAddingAddress(false);
      setIsAddInComparison(true);
      handleEditAddress(address);
    }
  };

  // Triggers the primary toggler
  const handleTogglePrimary = async (index) => {
    const primaryCount = addresses.filter(
      (address) => address.primary === 1
    ).length;

    const updatedAddresses = addresses.map((address, i) => {
      if (i === index) {
        if (address.primary === 1 && primaryCount === 1) {
          setShowWarningModal(true);
          setPendingPrimaryIndex(index);
          return address;
        } else {
          if (billingData === true) {
            setPreviousAddresses(addresses);
            setShowBillingWarningModal(true);
            return address;
          } else {
            const newPrimary = address.primary ? 0 : 1;
            const updatedAddress = {
              ...address,
              primary: newPrimary,
              bitwiseType: calculateBitwiseType({
                primary: newPrimary,
                mailing: address.mailing,
                shipping: address.shipping,
                billing: address.billing,
              }),
            };
            handleUpdateAddress(updatedAddress);
            return updatedAddress;
          }
        }
      } else if (address.primary === 1 && index !== i) {
        // Unsetting as primary address
        const updatedAddress = {
          ...address,
          primary: 0,
          bitwiseType: calculateBitwiseType({
            primary: 0,
            mailing: address.mailing,
            shipping: address.shipping,
            billing: address.billing,
          }),
        };
        // handleUpdateAddress(updatedAddress);
        return updatedAddress;
      }
      return address;
    });

    setAddresses(updatedAddresses);
    setIsTogglerDisabled(true);
    setTimeout(() => {
      if (!isLoading && addressData) {
        setIsTogglerDisabled(false);
      }
    }, 4000);
  };

  useEffect(() => {
    if (!isLoading && addressData) {
      setIsTogglerDisabled(false);
    }
  }, [isLoading, addressData]);

  const handleBillingWarningClose = () => {
    setShowBillingWarningModal(false);
    setAddresses(previousAddresses);
  };

  // Triggers the radio button default as shipping
  const handleDefaultShipping = (index) => {
    const updatedAddresses = addresses.map((address, i) => {
      if (i === index) {
        // Toggle the shipping status
        const newShipping = address.shipping === 1 ? 0 : 1;

        return {
          ...address,
          shipping: newShipping,
          bitwiseType: calculateBitwiseType({
            primary: address.primary,
            mailing: address.mailing,
            shipping: newShipping,
            billing: address.billing,
          }),
        };
      } else {
        return { ...address, shipping: 0 };
      }
    });

    setAddresses(updatedAddresses);
    //Update the data
    handleUpdateAddress(updatedAddresses[index]);
  };

  // API calls to delete address
  async function handleDeleteAddress() {
    try {
      await deleteAddress.mutateAsync(selectedAddress.iboAddressId);
      resetForm();
      setShowDeleteConfirmation(false);
    } catch (error) {
      console.error(t("Error deleting address"), error);
    }
    resetForm();
  }

  // Triggers when clicking the trash button
  const handleDelete = (address) => {
    setSelectedAddress(address);
    setShowDeleteConfirmation(true);
  };

  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
    resetForm();
  };

  // Triggers cancel button from comparison modal
  const handleComparisonCancelClick = () => {
    resetForm();
    setShowComparisonModal(false);
    history.push("/addresses");
  };

  const resetForm = () => {
    setAddress({
      recipient: "",
      country: "",
      line1: "",
      line2: "",
      city: "",
      state: "",
      postalCode: "",
    });

    setSelectedAddress({
      recipient: "",
      country: "",
      line1: "",
      line2: "",
      city: "",
      state: "",
      postalCode: "",
    });
    setValidationErrors({});
    setValidationFailed(false);
    history.push("/addresses");
  };

  // Triggers add address button
  const handleAddAddressOnClick = () => {
    setIsAddingAddress(true);
    setShowModal(true);
    resetForm();
  };

  // Triggers cancel button from address modal
  const handleCancel = () => {
    setShowModal(false);
    resetForm();
    history.push("/addresses");
  };

  const handleHeaderBackClick = () => {
    history.push("/");
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <Header
            header={t("Settings")}
            onClick={handleHeaderBackClick}
            disabled={isLoading || !addressData || isTogglerDisabled}
          />
        </div>
        <Button
          buttonText={t("Add Address")}
          onClick={handleAddAddressOnClick}
          className={styles.button}
          disabled={isLoading}
        />
        {isLoading && (
          <div>
            <Loader color={"#415364"} size={85} />
          </div>
        )}
        <div>
          <AddressList
            addresses={addresses}
            onDefault={handleDefaultShipping}
            onPrimary={handleTogglePrimary}
            onEdit={handleEditAddress}
            onDelete={handleDelete}
            isTogglerDisabled={isLoading || !addressData || isTogglerDisabled}
          />
        </div>

        {showDeleteConfirmation && (
          <Modal isOpen={showDeleteConfirmation}>
            <div className={styles.deleteContent}>
              <p className={styles.deleteHeader}>
                {t("Are you sure you want to delete this address")}
              </p>
              <div className={styles.deleteBody}>
                <AddressInfoItem
                  address={selectedAddress}
                  labelColor={"#91B6BB"}
                />
              </div>

              <div className={styles.buttonContainer}>
                <Button
                  buttonText={t("Yes")}
                  onClick={() => handleDeleteAddress(address)}
                />
                <Button buttonText={t("No")} onClick={cancelDelete} />
              </div>
            </div>
          </Modal>
        )}

        {isLoading ? (
          <div className={styles.loader}>
            <Loader color={"#415364"} size={85} />
          </div>
        ) : (
          <ComparisonModal
            isOpen={showComparisonModal}
            title={
              isAddInComparison
                ? t("Please Confirm Your Address")
                : t("Update Address")
            }
            isAdd={isAddInComparison}
            onClose={handleComparisonCancelClick}
            suggestedAddress={suggestedAddress}
            address={address}
            handleUseSuggested={handleUseSuggested}
            handleUseOriginal={handleUseOriginal}
            setShowUpdateForm={false}
            setShowComparisonModal={setShowComparisonModal}
            handleEditClick={handleComparisonEditClick}
            loading={loading}
          />
        )}

        {isLoading ? (
          <div className={styles.loader}>
            <Loader color={"#415364"} size={85} />
          </div>
        ) : (
          <AddressModal
            title={isAddingAddress ? t("Add Address") : t("Edit Address")}
            isAdd={isAddingAddress}
            isOpen={showModal}
            address={selectedAddress}
            states={statesData}
            onCancel={handleCancel}
            onValidate={(address) =>
              handleValidateAddress(address, isAddingAddress)
            }
            onChange={handleChange}
            validationFailed={validationFailed}
            validationErrorsArray={validationErrors}
            loading={loading}
          />
        )}

        {showWarningModal && (
          <WarningPrimaryModal
            isOpen={showWarningModal}
            title={t("You are required to have a default")}
            description={t("Please add a new address")}
            onClick={() => {
              setShowWarningModal(false);
            }}
          />
        )}

        {showBillingWarningModal && (
          <WarningPrimaryModal
            isOpen={showBillingWarningModal}
            title={t("Primary Address Change Restricted")}
            description={t("The ability to change address is disabled")}
            onClick={handleBillingWarningClose}
          />
        )}
      </div>
    </>
  );
}

export default AddressesPage;
